<template>
  <div class="wrapper">
    <div class="card card-custom shadow-none">
      <div
        class="card-body d-flex align-items-center justify-content-between px-0"
      >
        <div class="input-icon">
          <input
            type="text"
            class="form-control rounded-pill"
            placeholder="Search..."
            :disabled="disabled"
            v-model="term"
            @input="search"
          />

          <span class="mt-1 mx-2">
            <i class="material-icons text-muted">search</i>
          </span>
        </div>

        <b-button
          v-if="$permissions.businesses.create"
          variant="dark"
          class="btn-pill font-weight-bolder"
          :disabled="disabled"
          @click="create"
          >New Company</b-button
        >
      </div>
    </div>

    <!-- Companies Table -->
    <div class="card card-custom shadow-none">
      <div class="card-body p-0">
        <div class="blockui shadow-sm zindex-1" v-if="querying">
          <span class="btn spinner spinner-lg spinner-dark spinner-left">Loading...</span>
        </div>

        <div class="d-flex align-items-center justify-content-center p-4" v-if="empty">
          <span v-if="querying">...</span>
          <span v-else-if="error">{{ error }}</span>
          <span v-else>No results found.</span>
        </div>

        <div v-if="!querying && !empty" class="table-responsive">
          <table class="table table-hover table-striped">
            <thead class="thead-light">
              <tr>
                <th class="border-0" style="width: 250px; max-width: 250px;">
                  <i class="las la-building mr-2"></i>Business
                </th>
                <th class="border-0">
                  <i class="las la-user mr-2"></i>Admin
                </th>
                <th class="border-0">
                  <i class="las la-envelope mr-2"></i>Email
                </th>
                <th class="border-0">
                  <i class="las la-globe mr-2"></i>Country
                </th>
                <th class="border-0">
                  <i class="las la-dollar-sign mr-2"></i>Currency
                </th>
                <th class="border-0">
                  <i class="las la-eye mr-2"></i>Status
                </th>
                <th class="border-0 text-center">
                  <i class="las la-cog"></i>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="(object, i) in objects" 
                :key="i"
                class="clickable-row"
                @click="moveToCompany(object.id)"
                :id="`card_${i}`"
              >
                <td class="align-middle" style="width: 150px; max-width: 150px; word-wrap: break-word; white-space: normal;">
                  <div class="font-weight-bold">{{ object.name }}</div>
                </td>
                <td class="align-middle">{{ (object.admin_user && object.admin_user.first_name && object.admin_user.last_name) ? (object.admin_user.first_name + ' ' + object.admin_user.last_name) : 'N/A' }}</td>
                <td class="align-middle">
                  <a v-if="object.admin_user && object.admin_user.email" :href="`mailto:${object.admin_user.email}`" class="text-primary" @click.stop>
                    {{ object.admin_user.email }}
                  </a>
                  <span v-else>N/A</span>
                </td>
                <td class="align-middle">
                  <span class="badge badge-light">{{ object.country || 'N/A' }}</span>
                </td>
                <td class="align-middle">
                  <span class="badge badge-light">{{ object.base_currency || 'N/A' }}</span>
                </td>
                <td class="align-middle">
                  <span 
                    class="badge"
                    :class="object.is_active ? 'badge-success' : 'badge-danger'"
                  >
                    {{ object.active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td class="align-middle text-center">
                  <button 
                    class="btn btn-sm btn-light btn-icon" 
                    @click.stop="moveToCompanySettings(object.id)"
                    title="Company Settings"
                    v-b-tooltip.hover
                  >
                    <i class="las la-cog"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <form-modal ref="form_modal" :term="term" @close="close" />
        <c-details ref="details-modal" @preview="previewNewsletter()" @close="close" />
        <newsletter ref="newsletter-modal" />
      </div>
    </div>
    <base-pagination
      :activePage="1"
      :totalRows="totalRows"
      @set-page="setPage"
    ></base-pagination>
  </div>
</template>

<script>
import CompaniesService from "@/services/companies.service";
import { GET_ALL_COMPANIES } from "@/store/companies";
import { mapGetters, mapState } from 'vuex';
import FormModal from "./components/form";
import CDetails from "./components/details";
import Newsletter from "./components/newsletter.vue";
import { validateAdminQueryParameter } from "@/store/invoice/helpers";
import { QUERY_PARAMETERS_ACTIONS } from "@/store/queryparams";
import BasePagination from "@/components/UI/BasePagination.vue";
import { QUERY_PARAMETERS } from "@/constants";

export default {
  name: "Companies",
  components: {
    BasePagination,
    FormModal,
    CDetails,
    Newsletter
  },
  created(){
    this.$store.dispatch(QUERY_PARAMETERS_ACTIONS.RESET_QUERY_PARAMS);
    if(this.$route.query.search){
        this.term = this.$route.query.search;
        let params={}
        params.search = this.$route.query.search;
        this.query(params);
    }
  },
  methods: {
    updateInvoices(params) {
      this.querying = true;
      this.$store.dispatch(GET_ALL_COMPANIES, {
        params: { ...params },
      })
        .finally(() => {
          this.querying = false;
        });
    },
    setPage(pageNoOrType) {
      this.querying = true;
      this.$store.dispatch(GET_ALL_COMPANIES, {
        [QUERY_PARAMETERS.PAGE_NO]: pageNoOrType,
        [QUERY_PARAMETERS.PAGE_SIZE]: 20,
      })
        .finally(() => {
          this.querying = false;
        });
    },
    previewNewsletter(){
      this.$refs["newsletter-modal"].open(this.currentBusiness.id);
    },
    moveToCompany(id) {
      this.$router.push({name: 'Company', params:{business_id: id}})
    },
    moveToCompanySettings(id) {
      this.$router.push({name: 'CompanySettings', params:{business_id: id}})
    },
    search() {
      clearTimeout(this.debounce);

      this.debounce = setTimeout(() => {
        let params = {};

        if (this.term) params.search = this.term;

        this.query(params);
      }, 500);
    },
    query(params = {}) {
      params["page"] = this.$route.query.page || 1;
      params["page_size"] = this.page_size;
      let currentPath = this.$route.path;
      this.$store.dispatch(QUERY_PARAMETERS_ACTIONS.RESET_QUERY_PARAMS);
      if (this.term) {
        params.search = this.term;
        const payload = validateAdminQueryParameter({'search':this.term},0);
        this.$store.dispatch(QUERY_PARAMETERS_ACTIONS.ADD_FILTER_QUERY_PARAMETER, {payload, currentPath})
      }
      else{
        this.$store.dispatch(QUERY_PARAMETERS_ACTIONS.REMOVE_PARAMS_FROM_URL, this.$route.path);
      }
      this.error = null;
      this.querying = true;
      this.$store.dispatch(GET_ALL_COMPANIES, params)
        .then((data) => {
          this.totalRows = data.count
        })
        .catch(({ message }) => {
          this.error = "There was an error loading companies.";
          this.$showAlert("There was an error loading companies.",'danger');
        })
        .finally(() => {
          this.querying = false;
        });
    },
    view(business) {
      this.currentBusiness = business;
      this.$refs["details-modal"].open(business);
    },
    close(){
      this.query();
    },
    create() {
      this.$refs.form_modal.show();
    },
  },
  computed: {
    empty() {
      return this.objects.length === 0;
    },
    disabled() {
      return this.querying || this.deleting;
    },
  },
  data() {
    return {
      baseURL: process.env.VUE_APP_API_URL,
      debounce: null,
      term: null,
      error: null,
      querying: false,
      deleting: false,
      total: 0,
      page_size: 20,
      objects: [],
      currentBusiness:{},
      totalRows: 0,
    };
  },
  mounted() {
    if (!this.$permissions.businesses.read) this.$router.push({ name: "403" });
    this.query();
  },
  watch: {
    "$route.query.page"(value) {
      this.query();
    },
    '$store.state.companies.companies'(val){
      this.objects = val;
    }
  }
};
</script>

<style scoped>
@import "./index.scss"
</style>
